/*
=========================================================
Código hecho por Rcrea
RCREA COLLECTION SLIDER

DESKTOP
- Marquee constante con requestAnimationFrame.
- Loop infinito.
- Clones automáticos.
- Velocidad Shopify.
- Pausa hover opcional.

MOBILE
- Marquee desactivado completamente.
- Nunca utiliza clones.
- Carrusel paginado.
- Flechas.
- Dots automáticos.
- Swipe horizontal.
- Sin loop al llegar a extremos.
- Número de dots calculado según ancho real.
=========================================================
*/

(() => {

  class RcreaCollectionSlider {

    constructor(shell) {

      this.shell = shell;

      this.section =
        shell.closest(
          ".rcrea-collection-slider"
        );


      this.track =
        shell.querySelector(
          "[data-rcrea-track]"
        );


      this.viewport =
        shell.querySelector(
          "[data-rcrea-mobile-viewport]"
        );


      this.originalItems =
        Array.from(
          shell.querySelectorAll(
            "[data-rcrea-original-item]"
          )
        );


      /* =====================================================
         DESKTOP SETTINGS
      ===================================================== */

      this.enabled =
        shell.dataset.autoplay ===
        "true";


      this.pauseOnHover =
        shell.dataset.pauseHover ===
        "true";


      this.speed =
        Number(
          shell.dataset.loopSpeed
        ) || 35;


      /* =====================================================
         MOBILE SETTINGS
      ===================================================== */

      this.mobileSwipe =
        shell.dataset.mobileSwipe !==
        "false";


      this.mobileQuery =
        window.matchMedia(
          "(max-width: 749px)"
        );


      /* =====================================================
         MOBILE CONTROLS
      ===================================================== */

      this.mobileControls =
        this.section?.querySelector(
          "[data-rcrea-mobile-controls]"
        );


      this.mobilePrev =
        this.section?.querySelector(
          "[data-rcrea-mobile-prev]"
        );


      this.mobileNext =
        this.section?.querySelector(
          "[data-rcrea-mobile-next]"
        );


      this.mobileDots =
        this.section?.querySelector(
          "[data-rcrea-mobile-dots]"
        );


      /* =====================================================
         DESKTOP LOOP STATE
      ===================================================== */

      this.raf = null;

      this.lastTime = null;

      this.translateX = 0;

      this.loopDistance = 0;

      this.isPaused = false;


      /* =====================================================
         MOBILE STATE
      ===================================================== */

      this.mobilePage = 0;

      this.mobilePageCount = 1;

      this.mobileStep = 0;

      this.mobileMaxTranslate = 0;


      /* =====================================================
         TOUCH STATE
      ===================================================== */

      this.touchStartX = null;

      this.touchStartY = null;

      this.touchStartTime = null;

      this.swipeThreshold = 42;

      this.swipeMaxDuration = 850;

      this.suppressClickUntil = 0;


      /* =====================================================
         MISC
      ===================================================== */

      this.mode = null;

      this.resizeTimer = null;


      if (
        !this.track ||
        !this.viewport ||
        !this.originalItems.length
      ) {
        return;
      }


      /* =====================================================
         BIND CONTEXT
      ===================================================== */

      this.handleResize =
        this.handleResize.bind(this);


      this.handleVisibility =
        this.handleVisibility.bind(this);


      this.handleTouchStart =
        this.handleTouchStart.bind(this);


      this.handleTouchEnd =
        this.handleTouchEnd.bind(this);


      this.handleTouchCancel =
        this.handleTouchCancel.bind(this);


      this.handleCapturedClick =
        this.handleCapturedClick.bind(this);


      /* =====================================================
         INIT
      ===================================================== */

      this.bindEvents();

      this.setupMode(true);


      /*
      Las fuentes pueden modificar mínimamente
      las medidas internas.

      Recalculamos una vez listas.
      */

      if (
        document.fonts &&
        document.fonts.ready
      ) {

        document.fonts.ready.then(
          () => {

            if (
              this.mobileQuery.matches
            ) {

              this.measureMobile();

            }

          }
        );

      }

    }


    /* =====================================================
       EVENTS
    ====================================================== */

    bindEvents() {

      /* DESKTOP HOVER */

      if (
        this.pauseOnHover
      ) {

        this.shell.addEventListener(
          "mouseenter",
          this.pause
        );


        this.shell.addEventListener(
          "mouseleave",
          this.resume
        );

      }


      /* PAGE VISIBILITY */

      document.addEventListener(
        "visibilitychange",
        this.handleVisibility
      );


      /* RESIZE */

      window.addEventListener(
        "resize",
        this.handleResize
      );


      /* MOBILE ARROWS */

      if (
        this.mobilePrev
      ) {

        this.mobilePrev.addEventListener(
          "click",
          () => {

            this.mobilePrevious();

          }
        );

      }


      if (
        this.mobileNext
      ) {

        this.mobileNext.addEventListener(
          "click",
          () => {

            this.mobileNextPage();

          }
        );

      }


      /* MOBILE SWIPE */

      this.viewport.addEventListener(
        "touchstart",
        this.handleTouchStart,
        {
          passive: true
        }
      );


      this.viewport.addEventListener(
        "touchend",
        this.handleTouchEnd,
        {
          passive: false
        }
      );


      this.viewport.addEventListener(
        "touchcancel",
        this.handleTouchCancel,
        {
          passive: true
        }
      );


      /*
      Evita abrir una colección cuando el usuario
      realmente hizo swipe sobre una tarjeta.
      */

      this.shell.addEventListener(
        "click",
        this.handleCapturedClick,
        true
      );

    }


    /* =====================================================
       UNBIND
    ====================================================== */

    unbindEvents() {

      if (
        this.pauseOnHover
      ) {

        this.shell.removeEventListener(
          "mouseenter",
          this.pause
        );


        this.shell.removeEventListener(
          "mouseleave",
          this.resume
        );

      }


      document.removeEventListener(
        "visibilitychange",
        this.handleVisibility
      );


      window.removeEventListener(
        "resize",
        this.handleResize
      );


      this.viewport.removeEventListener(
        "touchstart",
        this.handleTouchStart
      );


      this.viewport.removeEventListener(
        "touchend",
        this.handleTouchEnd
      );


      this.viewport.removeEventListener(
        "touchcancel",
        this.handleTouchCancel
      );


      this.shell.removeEventListener(
        "click",
        this.handleCapturedClick,
        true
      );

    }


    /* =====================================================
       MODE SWITCH
    ====================================================== */

    setupMode(force = false) {

      const nextMode =
        this.mobileQuery.matches
          ? "mobile"
          : "desktop";


      if (
        !force &&
        this.mode === nextMode
      ) {

        if (
          nextMode === "mobile"
        ) {

          this.measureMobile();

        }

        return;

      }


      this.stop();


      this.destroyClones();


      this.track.style.transform =
        "translate3d(0, 0, 0)";


      this.translateX = 0;

      this.lastTime = null;


      this.mode =
        nextMode;


      if (
        nextMode === "mobile"
      ) {

        this.setupMobile();

      } else {

        this.setupDesktop();

      }

    }


    /* =====================================================
       DESKTOP SETUP
    ====================================================== */

    setupDesktop() {

      this.shell.classList.remove(
        "is-mobile-carousel",
        "is-no-transition"
      );


      /*
      Ocultar controles mobile.
      */

      if (
        this.mobileControls
      ) {

        this.mobileControls.hidden =
          true;

      }


      /*
      Hasta dos elementos:
      centrado estático.
      */

      if (
        this.originalItems.length <= 2
      ) {

        this.shell.classList.add(
          "is-static"
        );


        this.shell.classList.remove(
          "is-loop"
        );


        this.track.style.transform =
          "none";


        return;

      }


      /*
      Más de dos elementos.
      */

      this.shell.classList.remove(
        "is-static"
      );


      if (
        !this.enabled
      ) {

        this.shell.classList.remove(
          "is-loop"
        );


        this.track.style.transform =
          "translate3d(0, 0, 0)";


        return;

      }


      this.shell.classList.add(
        "is-loop"
      );


      this.createClones();


      this.measureDesktop();


      if (
        this.loopDistance > 0
      ) {

        this.play();

      }

    }


    /* =====================================================
       MOBILE SETUP
    ====================================================== */

    setupMobile() {

      /*
      Mobile nunca conserva clases
      desktop.
      */

      this.shell.classList.remove(
        "is-loop",
        "is-static"
      );


      this.shell.classList.add(
        "is-mobile-carousel"
      );


      /*
      Seguridad extra:
      jamás dejamos clones en mobile.
      */

      this.destroyClones();


      this.mobilePage = 0;


      this.shell.classList.add(
        "is-no-transition"
      );


      this.track.style.transform =
        "translate3d(0, 0, 0)";


      window.requestAnimationFrame(
        () => {

          this.measureMobile();


          window.requestAnimationFrame(
            () => {

              this.shell.classList.remove(
                "is-no-transition"
              );

            }
          );

        }
      );

    }


    /* =====================================================
       DESKTOP CLONES
    ====================================================== */

    stripShopifyEditorAttributes(
      element
    ) {

      if (
        !element ||
        !element.attributes
      ) {
        return;
      }


      Array.from(
        element.attributes
      ).forEach(
        (attribute) => {

          if (
            attribute.name.startsWith(
              "data-shopify"
            )
          ) {

            element.removeAttribute(
              attribute.name
            );

          }

        }
      );


      Array.from(
        element.children
      ).forEach(
        (child) => {

          this.stripShopifyEditorAttributes(
            child
          );

        }
      );

    }


    createCloneSet() {

      const fragment =
        document.createDocumentFragment();


      this.originalItems.forEach(
        (item) => {

          const clone =
            item.cloneNode(true);


          clone.removeAttribute(
            "data-rcrea-original-item"
          );


          clone.setAttribute(
            "data-rcrea-clone",
            "true"
          );


          clone.setAttribute(
            "aria-hidden",
            "true"
          );


          clone.setAttribute(
            "tabindex",
            "-1"
          );


          this.stripShopifyEditorAttributes(
            clone
          );


          fragment.appendChild(
            clone
          );

        }
      );


      this.track.appendChild(
        fragment
      );

    }


    createClones() {

      /*
      Nunca clonar mobile.
      */

      if (
        this.mobileQuery.matches
      ) {
        return;
      }


      this.destroyClones();


      this.createCloneSet();


      const viewportWidth =
        this.viewport.offsetWidth ||
        window.innerWidth;


      let safety = 0;


      while (
        this.track.scrollWidth <
          viewportWidth * 3 &&
        safety < 8
      ) {

        this.createCloneSet();

        safety += 1;

      }

    }


    destroyClones() {

      this.track
        .querySelectorAll(
          "[data-rcrea-clone]"
        )
        .forEach(
          (clone) => {

            clone.remove();

          }
        );

    }


    /* =====================================================
       DESKTOP MEASURE
    ====================================================== */

    measureDesktop() {

      const firstOriginal =
        this.track.querySelector(
          "[data-rcrea-original-item]"
        );


      const firstClone =
        this.track.querySelector(
          "[data-rcrea-clone]"
        );


      if (
        !firstOriginal ||
        !firstClone
      ) {

        this.loopDistance = 0;

        return;

      }


      this.loopDistance =
        firstClone.offsetLeft -
        firstOriginal.offsetLeft;


      this.translateX = 0;


      this.track.style.transform =
        "translate3d(0, 0, 0)";

    }


    /* =====================================================
       DESKTOP PLAY
    ====================================================== */

    play() {

      this.stop();


      if (
        this.mobileQuery.matches ||
        !this.enabled ||
        this.loopDistance <= 0
      ) {

        return;

      }


      this.lastTime = null;


      const animate =
        (currentTime) => {


          if (
            this.mobileQuery.matches
          ) {

            this.stop();

            return;

          }


          if (
            !this.lastTime
          ) {

            this.lastTime =
              currentTime;

          }


          const delta =
            currentTime -
            this.lastTime;


          this.lastTime =
            currentTime;


          if (
            !this.isPaused &&
            this.loopDistance > 0
          ) {

            this.translateX -=
              (
                this.speed *
                delta
              )
              / 1000;


            if (
              Math.abs(
                this.translateX
              ) >=
              this.loopDistance
            ) {

              this.translateX +=
                this.loopDistance;

            }


            this.track.style.transform =
              `translate3d(${this.translateX}px, 0, 0)`;

          }


          this.raf =
            window.requestAnimationFrame(
              animate
            );

        };


      this.raf =
        window.requestAnimationFrame(
          animate
        );

    }


    /* =====================================================
       STOP RAF
    ====================================================== */

    stop() {

      if (
        this.raf
      ) {

        window.cancelAnimationFrame(
          this.raf
        );


        this.raf = null;

      }

    }


    /* =====================================================
       DESKTOP PAUSE
    ====================================================== */

    pause = () => {

      if (
        this.mobileQuery.matches
      ) {
        return;
      }


      this.isPaused = true;


      this.shell.classList.add(
        "is-paused"
      );

    };


    resume = () => {

      if (
        this.mobileQuery.matches
      ) {
        return;
      }


      this.isPaused = false;


      this.shell.classList.remove(
        "is-paused"
      );

    };


    /* =====================================================
       VISIBILITY
    ====================================================== */

    handleVisibility() {

      if (
        this.mobileQuery.matches
      ) {
        return;
      }


      if (
        document.hidden
      ) {

        this.pause();

      } else {

        this.resume();

      }

    }


    /* =====================================================
       MOBILE MEASURE
    ====================================================== */

    measureMobile() {

      if (
        !this.mobileQuery.matches
      ) {
        return;
      }


      /*
      Seguridad:
      mobile no puede contener clones.
      */

      this.destroyClones();


      const firstItem =
        this.originalItems[0];


      if (
        !firstItem
      ) {

        this.hideMobileControls();

        return;

      }


      const trackStyle =
        window.getComputedStyle(
          this.track
        );


      const gap =
        parseFloat(
          trackStyle.columnGap ||
          trackStyle.gap
        ) || 0;


      const itemWidth =
        firstItem
          .getBoundingClientRect()
          .width;


      this.mobileStep =
        itemWidth + gap;


      const viewportWidth =
        this.viewport.clientWidth;


      const trackWidth =
        this.track.scrollWidth;


      this.mobileMaxTranslate =
        Math.max(
          0,
          trackWidth -
          viewportWidth
        );


      /*
      Si no existe overflow no necesitamos
      arrows ni dots.
      */

      if (
        this.mobileMaxTranslate <= 2 ||
        this.mobileStep <= 0
      ) {

        this.mobilePage = 0;

        this.mobilePageCount = 1;


        this.track.style.transform =
          "translate3d(0, 0, 0)";


        this.hideMobileControls();

        return;

      }


      /*
      Cantidad de posiciones reales.

      Ejemplo:
      viewport muestra 3 colecciones,
      pero existen 7.

      No creamos 7 dots necesariamente:
      creamos sólo las posiciones requeridas
      hasta alcanzar el extremo final.
      */

      this.mobilePageCount =
        Math.ceil(
          this.mobileMaxTranslate /
          this.mobileStep
        ) + 1;


      this.mobilePage =
        Math.max(
          0,
          Math.min(
            this.mobilePage,
            this.mobilePageCount - 1
          )
        );


      this.renderMobileDots();


      this.showMobileControls();


      this.updateMobilePosition(
        false
      );

    }


    /* =====================================================
       MOBILE DOTS
    ====================================================== */

    renderMobileDots() {

      if (
        !this.mobileDots
      ) {
        return;
      }


      this.mobileDots.innerHTML =
        "";


      for (
        let index = 0;
        index < this.mobilePageCount;
        index += 1
      ) {

        const dot =
          document.createElement(
            "button"
          );


        dot.type =
          "button";


        dot.className =
          "rcrea-collection-slider__mobile-dot";


        dot.dataset.page =
          String(index);


        dot.setAttribute(
          "role",
          "tab"
        );


        dot.setAttribute(
          "aria-label",
          `Ir al grupo de categorías ${index + 1}`
        );


        dot.setAttribute(
          "aria-selected",
          index === this.mobilePage
            ? "true"
            : "false"
        );


        if (
          index === this.mobilePage
        ) {

          dot.classList.add(
            "is-active"
          );

        }


        dot.addEventListener(
          "click",
          () => {

            this.goToMobilePage(
              index
            );

          }
        );


        this.mobileDots.appendChild(
          dot
        );

      }

    }


    /* =====================================================
       MOBILE POSITION
    ====================================================== */

    updateMobilePosition(
      animate = true
    ) {

      if (
        !this.mobileQuery.matches
      ) {
        return;
      }


      if (
        !animate
      ) {

        this.shell.classList.add(
          "is-no-transition"
        );

      }


      let translate =
        this.mobilePage *
        this.mobileStep;


      /*
      El último paso se ajusta al borde exacto
      del contenido para no dejar hueco.
      */

      translate =
        Math.min(
          translate,
          this.mobileMaxTranslate
        );


      this.track.style.transform =
        `translate3d(${-translate}px, 0, 0)`;


      this.updateMobileControlsState();


      if (
        !animate
      ) {

        window.requestAnimationFrame(
          () => {

            window.requestAnimationFrame(
              () => {

                this.shell.classList.remove(
                  "is-no-transition"
                );

              }
            );

          }
        );

      }

    }


    /* =====================================================
       MOBILE GO TO PAGE
    ====================================================== */

    goToMobilePage(
      page
    ) {

      if (
        !this.mobileQuery.matches
      ) {
        return;
      }


      const safePage =
        Math.max(
          0,
          Math.min(
            page,
            this.mobilePageCount - 1
          )
        );


      if (
        safePage === this.mobilePage
      ) {

        this.updateMobileControlsState();

        return;

      }


      this.mobilePage =
        safePage;


      this.updateMobilePosition(
        true
      );

    }


    /* =====================================================
       MOBILE PREVIOUS
    ====================================================== */

    mobilePrevious() {

      if (
        !this.mobileQuery.matches ||
        this.mobilePage <= 0
      ) {
        return;
      }


      this.goToMobilePage(
        this.mobilePage - 1
      );

    }


    /* =====================================================
       MOBILE NEXT
    ====================================================== */

    mobileNextPage() {

      if (
        !this.mobileQuery.matches ||
        this.mobilePage >=
          this.mobilePageCount - 1
      ) {
        return;
      }


      this.goToMobilePage(
        this.mobilePage + 1
      );

    }


    /* =====================================================
       MOBILE CONTROLS STATE
    ====================================================== */

    updateMobileControlsState() {

      if (
        !this.mobileQuery.matches
      ) {
        return;
      }


      if (
        this.mobilePrev
      ) {

        this.mobilePrev.disabled =
          this.mobilePage <= 0;

      }


      if (
        this.mobileNext
      ) {

        this.mobileNext.disabled =
          this.mobilePage >=
          this.mobilePageCount - 1;

      }


      if (
        this.mobileDots
      ) {

        const dots =
          Array.from(
            this.mobileDots.querySelectorAll(
              ".rcrea-collection-slider__mobile-dot"
            )
          );


        dots.forEach(
          (dot, index) => {

            const active =
              index ===
              this.mobilePage;


            dot.classList.toggle(
              "is-active",
              active
            );


            dot.setAttribute(
              "aria-selected",
              active
                ? "true"
                : "false"
            );

          }
        );

      }

    }


    /* =====================================================
       SHOW / HIDE MOBILE CONTROLS
    ====================================================== */

    showMobileControls() {

      if (
        this.mobileControls
      ) {

        this.mobileControls.hidden =
          false;

      }

    }


    hideMobileControls() {

      if (
        this.mobileControls
      ) {

        this.mobileControls.hidden =
          true;

      }


      if (
        this.mobileDots
      ) {

        this.mobileDots.innerHTML =
          "";

      }

    }


    /* =====================================================
       MOBILE TOUCH START
    ====================================================== */

    handleTouchStart(
      event
    ) {

      if (
        !this.mobileQuery.matches ||
        !this.mobileSwipe ||
        event.touches.length !== 1
      ) {

        this.resetTouch();

        return;

      }


      const touch =
        event.touches[0];


      this.touchStartX =
        touch.clientX;


      this.touchStartY =
        touch.clientY;


      this.touchStartTime =
        Date.now();

    }


    /* =====================================================
       MOBILE TOUCH END
    ====================================================== */

    handleTouchEnd(
      event
    ) {

      if (
        !this.mobileQuery.matches ||
        !this.mobileSwipe ||
        this.touchStartX === null ||
        this.touchStartY === null
      ) {

        this.resetTouch();

        return;

      }


      const touch =
        event.changedTouches[0];


      if (
        !touch
      ) {

        this.resetTouch();

        return;

      }


      const deltaX =
        touch.clientX -
        this.touchStartX;


      const deltaY =
        touch.clientY -
        this.touchStartY;


      const absX =
        Math.abs(
          deltaX
        );


      const absY =
        Math.abs(
          deltaY
        );


      const duration =
        Date.now() -
        this.touchStartTime;


      const isHorizontalSwipe =
        absX >=
          this.swipeThreshold &&

        absX >
          absY * 1.15 &&

        duration <=
          this.swipeMaxDuration;


      if (
        isHorizontalSwipe
      ) {

        /*
        Evitar que Safari abra el enlace
        de la categoría tras el swipe.
        */

        event.preventDefault();


        this.suppressClickUntil =
          Date.now() + 350;


        /*
        Swipe izquierda =
        siguiente grupo.
        */

        if (
          deltaX < 0
        ) {

          this.mobileNextPage();

        }


        /*
        Swipe derecha =
        grupo anterior.
        */

        if (
          deltaX > 0
        ) {

          this.mobilePrevious();

        }

      }


      this.resetTouch();

    }


    /* =====================================================
       TOUCH CANCEL
    ====================================================== */

    handleTouchCancel() {

      this.resetTouch();

    }


    resetTouch() {

      this.touchStartX = null;

      this.touchStartY = null;

      this.touchStartTime = null;

    }


    /* =====================================================
       PREVENT CLICK AFTER SWIPE
    ====================================================== */

    handleCapturedClick(
      event
    ) {

      if (
        !this.mobileQuery.matches
      ) {
        return;
      }


      if (
        Date.now() <
        this.suppressClickUntil
      ) {

        event.preventDefault();

        event.stopPropagation();

      }

    }


    /* =====================================================
       RESIZE
    ====================================================== */

    handleResize() {

      window.clearTimeout(
        this.resizeTimer
      );


      this.resizeTimer =
        window.setTimeout(
          () => {

            this.setupMode(
              false
            );

          },
          140
        );

    }


    /* =====================================================
       REBUILD
    ====================================================== */

    rebuild() {

      this.setupMode(
        true
      );

    }


    /* =====================================================
       DESTROY
    ====================================================== */

    destroy() {

      this.stop();


      this.unbindEvents();


      this.destroyClones();


      this.track.style.transform =
        "";


      this.hideMobileControls();


      this.shell.classList.remove(
        "is-loop",
        "is-static",
        "is-mobile-carousel",
        "is-no-transition",
        "is-paused"
      );

    }

  }


  /* =======================================================
     INIT
  ======================================================== */

  const initRcreaCollectionSliders =
    (root = document) => {


      root
        .querySelectorAll(
          "[data-rcrea-slider]"
        )
        .forEach(
          (shell) => {


            if (
              shell.dataset
                .rcreaInitialized ===
              "true"
            ) {
              return;
            }


            shell.dataset
              .rcreaInitialized =
              "true";


            shell.rcreaCollectionSlider =
              new RcreaCollectionSlider(
                shell
              );


          }
        );

    };


  /* =======================================================
     DOM READY
  ======================================================== */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      () => {

        initRcreaCollectionSliders();

      }
    );

  } else {

    initRcreaCollectionSliders();

  }


  /* =======================================================
     SHOPIFY SECTION LOAD
  ======================================================== */

  document.addEventListener(
    "shopify:section:load",
    (event) => {

      initRcreaCollectionSliders(
        event.target
      );

    }
  );


  /* =======================================================
     SHOPIFY SECTION UNLOAD
  ======================================================== */

  document.addEventListener(
    "shopify:section:unload",
    (event) => {


      const shell =
        event.target.querySelector(
          "[data-rcrea-slider]"
        );


      if (
        shell &&
        shell.rcreaCollectionSlider
      ) {

        shell
          .rcreaCollectionSlider
          .destroy();


        delete shell
          .rcreaCollectionSlider;


        delete shell.dataset
          .rcreaInitialized;

      }

    }
  );


  /* =======================================================
     SHOPIFY BLOCK SELECT

     Cuando editas una categoría dentro del editor,
     recalculamos medidas mobile.
  ======================================================== */

  document.addEventListener(
    "shopify:block:select",
    (event) => {


      const section =
        event.target.closest(
          ".rcrea-collection-slider"
        );


      const shell =
        section?.querySelector(
          "[data-rcrea-slider]"
        );


      if (
        shell &&
        shell.rcreaCollectionSlider
      ) {

        window.requestAnimationFrame(
          () => {

            shell
              .rcreaCollectionSlider
              .rebuild();

          }
        );

      }

    }
  );

})();